import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface LinkedContentSectionProps {
  title: string;
  icon: ReactNode;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}

/**
 * LinkedContentSection component provides an expandable section
 * for displaying user's linked content (businesses, jobs, etc.)
 * 
 * @param {LinkedContentSectionProps} props - Component properties
 * @returns {JSX.Element} Rendered linked content section
 */
export function LinkedContentSection({
  title,
  icon,
  count,
  expanded,
  onToggle,
  children,
}: LinkedContentSectionProps) {
  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-neutral-50 hover:bg-neutral-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-primary-600">{icon}</div>
          <div className="text-left">
            <p className="font-medium text-neutral-900">{title}</p>
            <p className="text-sm text-neutral-600">
              {count} {count === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="text-neutral-600" size={20} />
        ) : (
          <ChevronDown className="text-neutral-600" size={20} />
        )}
      </button>

      {expanded && (
        <div className="p-4 bg-white">
          {count === 0 ? (
            <p className="text-center py-4 text-neutral-500 text-sm">No items found</p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}
